import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import "./index.scss";

import { Popup, GraphDesignTool, ConfirmDialog, PerformerConstraints, ConfigContainer } from "components";
import { deleteNode, initialNodeCoords } from "components/GraphDesignTool";
import { AnalysisTaskConfig, AnalysisConnectionConfig, AnalysisGroupConfig } from "components/Config";
import { Text, Number, Container, ColorPicker, Title } from "components/Form";

import { message, analysisDefinitions, createMap, addToMap, removeFromMap, analysisGroups } from "state_management";
import { FiPlus, FiCircle, FiSettings } from "react-icons/fi";
import { FaArrowUp } from 'react-icons/fa';
import useNavigationBlocker from "utils/hooks/useBlocker"
import useT from "utils/hooks/useT"

// Configuration page for setting up the team's analyses.
export default function AnalysisConfigurationPage({ id }) {
  const { t } = useT("common")
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTask, setActiveTask] = useState(null);
  const [activeConnection, setActiveConnection] = useState(null);
  const [addConfig, setAddConfig] = useState(false);
  const [analysisGroupConfigId, setAnalysisGroupConfigId] = useState(null);
  const [showPerformerConstraints, setShowPerformerConstraints] = useState(false);

  const competenceGroups = useSelector((state) => state.competenceGroups);
  const analysisDefinition = useSelector((state) => state.analysisDefinitions[id]);
  const analysisNames = Object.values(useSelector((state) => state.analysisDefinitions)).map(
    (analysis) => analysis.name
  );
  const activeAnalysisGroupId = useSelector((state) => state.analysisGroups.activeAnalysisGroupId);
  const [isDraft, setIsDraft] = useState(true);

  const [tasks, setTasks] = useState(analysisDefinition ? createMap(analysisDefinition.taskDefinitions) : {});
  const [connections, setConnections] = useState(
    analysisDefinition ? createMap(analysisDefinition.taskDefinitionConnections) : {}
  );
  const [performerConstraints, setPerformerConstraints] = useState(
    analysisDefinition
      ? analysisDefinition.performerConstraints.map((constraint) => ({
          firstDefinitionId: constraint.firstDefinitionId,
          secondDefinitionId: constraint.secondDefinitionId,
          performerConstraint: constraint.performerConstraint,
        }))
      : []
  );


  const [color, setColor] = useState(analysisDefinition ? analysisDefinition.color : null);
  const [name, setName] = useState(analysisDefinition ? analysisDefinition.name : "");
  const [maxSampleCount, setMaxSampleCount] = useState(analysisDefinition ? analysisDefinition.maxSampleCount : null);
  const [minSampleCount, setMinSampleCount] = useState(analysisDefinition ? analysisDefinition.minSampleCount : 0);
  const [shouldCheckChanges, setShouldCheckChanges] = useState(true);
  const [leavePageObject, setLeavePageObject] = useState({ msg: "", location: "", display: false });
  const analysisGroupsDropdown = useSelector((state) => state.analysisGroups ? state.analysisGroups.groups : []);
  const [mode, setMode] = useState("node");
  const [nodeCoords, setNodeCoords] = useState(() => initialNodeCoords(tasks, connections));

  const [minTime, setMinTime] = useState(0);

  useEffect(() => {
    let perSampleDurationSum = 0;
    let setupTimeSum = 0;
    let minTimeSum = 0;

    Object.values(tasks).forEach(task => {
      perSampleDurationSum += task.perSampleDuration || 0;
      setupTimeSum += task.setuptime || 0;
    });

    Object.values(connections).forEach(connection => {
      minTimeSum += connection.timeBetween.min || 0;
    });

    minTimeSum += perSampleDurationSum + setupTimeSum;
    setMinTime(minTimeSum);
  }, [tasks, connections]);

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor(((seconds % 86400) % 3600) / 60);
    const formattedTime = `${days}d ${hours}h ${minutes}m`;
    return formattedTime;
  }

  //Decides whether or not the user has changed any fields on the page
  function somethingHasChanged(a) {
    var msg = ""

    if (!a) {
      msg = name ? msg.concat("- " + t("analysisName") + " ") : msg
      msg = color ? msg.concat("- " + t("color") + " ") : msg
      msg = maxSampleCount ? msg.concat("- " + t("maxSampleCount") + " ") : msg
      msg = minSampleCount ? msg.concat("- " + t("minSampleCount") + " ") : msg
      msg = performerConstraints.length ? msg.concat("- " + t("performerConstraintsCount") + " ") : msg
      msg = Object.values(tasks).length ? msg.concat("- " + t("taskCount") + " ") : msg
      msg = Object.values(connections).length ? msg.concat("- " + t("connectionCount") + " ") : msg
      return msg
    }
    //Analysis basic checks
    msg = a.color !== color ? msg.concat("- " + t("color") + " ") : msg
    msg = a.maxSampleCount !== maxSampleCount ? msg.concat("- " + t("maxSampleCount") + " ") : msg
    msg = a.minSampleCount !== minSampleCount ? msg.concat("- " + t("minSampleCount") + " ") : msg
    msg = a.name !== name ? msg.concat("- " + t("analysisName") + " ") : msg

    //Task Connection checks
    var connectionsA = a.taskDefinitionConnections
    var connectionsB = Object.values(connections)
    var Connections = false

    if (connectionsA.length === connectionsB.length) {
      for (let i = 0; i < connectionsA.length; i++) {
        Connections = connectionsA[i].id !== connectionsB[i].id ? true : Connections
        Connections = connectionsA[i].isWorkdayConnection !== connectionsB[i].isWorkdayConnection ? true : Connections
        Connections = connectionsA[i].nextId !== connectionsB[i].nextId ? true : Connections
        Connections = connectionsA[i].prevId !== connectionsB[i].prevId ? true : Connections
        Connections = connectionsA[i].samplePercentage !== connectionsB[i].samplePercentage ? true : Connections
        Connections = connectionsA[i].timeBetween.max !== connectionsB[i].timeBetween.max ? true : Connections
        Connections = connectionsA[i].timeBetween.min !== connectionsB[i].timeBetween.min ? true : Connections
      }
    } else {
      msg = msg.concat("- " + t("connectionCount"))
    }
    msg = Connections ? msg.concat("- " + t("connections")) : msg

    //Performer Constraint checks
    var constra = false
    if (a.performerConstraints.length === performerConstraints.length) {
      for (let i = 0; i < a.performerConstraints.length; i++) {
        constra =
          a.performerConstraints[i].firstDefinitionId !== performerConstraints[i].firstDefinitionId ? true : constra
        constra =
          a.performerConstraints[i].performerConstraint !== performerConstraints[i].performerConstraint ? true : constra
        constra =
          a.performerConstraints[i].secondDefinitionId !== performerConstraints[i].secondDefinitionId ? true : constra
      }
    } else {
      msg = msg.concat("- " + t("performerConstraintsCount"))
    }
    msg = constra ? msg.concat("- " + t("performerConstraintsLabel") + " ") : msg

    //Task definition checks
    let tasksA = a.taskDefinitions || [];
    let tasksB = Object.values(tasks || {});

    if (tasksA.length === tasksB.length) {
        tasksA.forEach((task, i) => {
            // Check for setup time and per sample duration at task level
            let setupTimeChanged = task.setuptime !== tasksB[i].setuptime;
            let sampleDurationChanged = task.perSampleDuration !== tasksB[i].perSampleDuration;

            // Check asset requirements within each task
            const assetReqA = task.assetRequirements || [];
            const assetReqB = tasksB[i].assetRequirements || [];
            const addedOrRemovedAsset = assetReqA.length !== assetReqB.length;

            const assetRequirementsChanged = assetReqA.length === assetReqB.length && assetReqA.some((assetReq, j) => {
                return [
                    assetReq.allowedAssetIds.length !== assetReqB[j].allowedAssetIds.length ||
                    assetReq.allowedAssetIds.some((id, k) => id !== assetReqB[j].allowedAssetIds[k]),
                    assetReq.assetId !== assetReqB[j].assetId,
                    assetReq.assetOffset !== assetReqB[j].assetOffset,
                    assetReq.assetPerSampleDuration !== assetReqB[j].assetPerSampleDuration,
                    assetReq.assetSetuptime !== assetReqB[j].assetSetuptime
                ].some(Boolean);
            });

            if (assetRequirementsChanged) {
                msg += "- " + t("assetRequirementsForAsset", { index: i + 1 }) + " ";
            }
            if (setupTimeChanged) {
                msg += "- " + t("setupTimeForPeopleSetup");
            }
            if (sampleDurationChanged) {
                msg += "- " + t("perSampleDurationForPeopleSetup");
            }
            if (addedOrRemovedAsset) {
                msg += "- " + t("assetAddedOrRemoved");
            }
        });
    } else {
        msg += "- " + t("taskCount") + " ";
    }

    return msg
  }

  function FEcheckconstraintMessage(res,isDraft, isNew) {
    if (isNew && analysisNames.includes(res.name)) {
      return t("analysisNameMustBeUnique")
    } else if(!res.name) {
      return t("pleaseFillOutAnalysisName")
    } else if(!res.color) {
      return t("pleaseChooseColor")
    } else if(res.minSampleCount !== null && res.maxSampleCount !== null && res.minSampleCount > res.maxSampleCount) {
      return t("minSampleMustBeLowerThanMax")
    } else if(!isDraft && res.taskDefinitions.length === 0) {
      return t("analysisMustContainTaskDefinition")
    }
    // when converting a draft to definition, or 'Save As' in definition
    if (!(isDraft || isNew) || (isNew && !isDraft)) {
      const taskFound = res.taskDefinitions.some(task => !task.name || task.allowedGroupIds.length === 0 || typeof(task.setuptime) !== 'number')
      if(taskFound) {
        return t("pleaseFillOutRequiredTaskParams")
      }
      return ""
   }
  }

  const isMountedRef = useRef(true)
  const shouldCheckChangesRef = useRef(true)

  useEffect(() => {
    shouldCheckChangesRef.current = shouldCheckChanges
  }, [shouldCheckChanges])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      shouldCheckChangesRef.current = false
    }
  }, [])

  const blocker = useNavigationBlocker(
    isMountedRef.current &&
    shouldCheckChangesRef.current &&
      !leavePageObject.display &&
      somethingHasChanged(analysisDefinition));
  if (blocker.state === "blocked" && !leavePageObject.display) {
    setLeavePageObject({
      msg: somethingHasChanged(analysisDefinition),
      location: blocker.tx?.nextLocation?.pathname || "",
      display: true,
    })
  }

  return (
    <>
    <ConfigContainer
      title={ analysisDefinition ? (analysisDefinition.isDraft ? t("analysisDraft") : t("analysisDefinition") ) : t("analysisDraft")}
      cancelHandler={() => {
          shouldCheckChangesRef.current = false
          setShouldCheckChanges(false)
          navigate("/setup/analyses")
        }}
      submitHandler={() => {
        setName(name.trim())
        shouldCheckChangesRef.current = false
        setShouldCheckChanges(false)
        const trimmedName = name.trim()
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          taskDefinitions: Object.values(tasks).map((task) => ({
            coordinates: nodeCoords[task.id],
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,false,false)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,false,false)))
        }
        if (!id) {
          dispatch(analysisDefinitions.create(res)).then(() => navigate("/setup/analyses"))
        } else {
          // Ugly quick fix to prevent duplication
          return dispatch(analysisDefinitions.edit(res, id)).then(() => navigate("/setup/analyses"))
        }
      }}
      saveAsHandler={() => {
        setIsDraft(false)
        setName(name.trim())
        const trimmedName = name.trim()
        setShouldCheckChanges(false)
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          taskDefinitions: Object.values(tasks).map((task) => ({
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,isDraft,true)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,isDraft,true)))
        }
        dispatch(analysisDefinitions.create(res)).then(() => navigate("/setup/analyses"))
      }}
      saveDraftHandler={() => {
        setIsDraft(true)
        setName(name.trim())
        shouldCheckChangesRef.current = false
        setShouldCheckChanges(false)
        const trimmedName = name.trim()
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          taskDefinitions: Object.values(tasks).map((task) => ({
            coordinates: nodeCoords[task.id],
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime ? task.setuptime : 0,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,isDraft,false)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,isDraft,false)))
        }
        if (!id) {
          dispatch(analysisDefinitions.createDraft(res)).then(() => navigate("/setup/analyses"))
        } else {
          // Ugly quick fix to prevent duplication
          return dispatch(analysisDefinitions.editDraft(res, id))
        }
      }}
      saveAsName={t("saveAsNewAnalysis")}
      submitButtonName={t("save")}
      cancelButtonName={t("cancel")}
      deleteIsImportant
      isDraft={analysisDefinition ? analysisDefinition.isDraft : true}
      deleteHandler={
        id ? () => {
            shouldCheckChangesRef.current = false
            setShouldCheckChanges(false)
            dispatch(analysisDefinitions.remove(id)).then(() => navigate("/setup/analyses"))
          }
            : null
        }
      >
        {leavePageObject.display && (
          <ConfirmDialog
            onConfirm={() => {
              if (blocker.state === "blocked") {
                blocker.proceed();
              } else {
                navigate(leavePageObject.location);
              }
            }}
            onCancel={() => {
              if (blocker.state === "blocked") {
                blocker.reset();
                setLeavePageObject({ ...leavePageObject, display: false });
              } else {
                setLeavePageObject({ ...leavePageObject, display: false });
              }
            }}
          >
            <b>{t("areYouSureWantToExit")}</b>
            <div className="configurationPage-popup-list-of-changes">
              <p>{t("unsavedChangesIn")}</p>
              <ul>
                {leavePageObject.msg.split("-").slice(1).map((message, index) => (
                  <li key={index}>{message.trim()}</li>
                ))}
              </ul>
            </div>
          </ConfirmDialog>
        )}
      <div className="AnalysisConfigurationPage-metadata-container">
        <Container>
          <Title className="AnalysisName">
            {t("analysisName")}<div className="red-text">*</div>
          </Title>
          <Text required value={name} onChange={setName} />
        </Container>
        <InputGroup className="Analysis-color">
          <Title>
            {t("color")}<div className="red-text">*</div>
          </Title>
          <ColorPicker required value={color} onChange={setColor} />
        </InputGroup>
        <div className="ConfigPage-group-sample-container">
          <div className="ConfigPage-analysis-group">
          <Title>
            {t("analysisGroup")}
          </Title>
          <Dropdown
            className="AnalysisGroup-selector"
            onSelect={(eventKey) => {
              if (eventKey === "add"){
                setAddConfig(true)
              } else if (eventKey === "noGroup"){
                dispatch(analysisGroups.changeActiveAnalysisGroupId(null))
              } else {
                dispatch(analysisGroups.changeActiveAnalysisGroupId(eventKey))
              }
            }}
          >
            <Dropdown.Toggle variant="primary">{activeAnalysisGroupId ? analysisGroupsDropdown[activeAnalysisGroupId].name : t("noGroup")}</Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.values(analysisGroupsDropdown).map((analysisGroup) => (
                <Dropdown.Item key={analysisGroup.id} eventKey={analysisGroup.id}>
                  {analysisGroup.name}<FiSettings className="AnalysisGroup-setting" onClick={() => {
                    setAddConfig(true)
                    setAnalysisGroupConfigId(analysisGroup.id)
                  }} />
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item key="add" eventKey="add">
                <FiPlus /> {t("addAnalysisGroup")}
              </Dropdown.Item>
              <Dropdown.Item key="noGroup" eventKey="noGroup">
                <FiCircle /> {t("noGroup")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          </div>
          <div className="ConfigPage-sample-count">
            <Title>
              {t("maxSampleCount")}<div className="red-text">*</div>
            </Title>
            <Number
              style={{
                textAlign: "center",
                width: maxSampleCount !== null ? `calc(30px + ${maxSampleCount.toString().length * 9}px)` : "30px",
              }}
              required={!isDraft}
              min={1}
              value={maxSampleCount}
              onChange={setMaxSampleCount}
            />
            <Title>{t("minSampleCount")}</Title>
            <Number
              style={{
                textAlign: "center",
                width: minSampleCount !== null ? `calc(30px + ${minSampleCount.toString().length * 9}px)` : "30px",
              }}
              min={0}
              value={minSampleCount}
              onChange={setMinSampleCount}
            />
          </div>
        </div>
      </div>
      <p className="Pheading">{t("analysisDesignTool")}</p>
      <p className="Pheading ConfigurationPage">{t("taskDesignTool")}</p>
      <div className="AnalysisConfigurationPage-drawing">
        <GraphDesignTool
          required
          nodes={tasks}
          edges={connections}
          nodeColor={color ? color.toUpperCase() : "#FFFFFF"}
          onClear={() => {
            setTasks({})
            setConnections({})
          }}
          onCreateNode={(task) => {
            setTasks({ ...tasks, [task.id]: task })
          }}
          onEditNode={(task) => setActiveTask(task)}
          onDeleteNode={(id) => {
            const [newTasks, newConnections] = deleteNode(tasks, connections, id)
            setTasks(newTasks)
            setConnections(newConnections)
            setPerformerConstraints(
              performerConstraints
                .filter((constraint) => constraint.firstDefinitionId !== id)
                .filter((constraint) => constraint.secondDefinitionId !== id)
            )
          }}
          onCreateEdge={(prevId, nextId) => {
            const conn = {
              prevId,
              nextId,
              id: `${prevId}-${nextId}`,
              timeBetween: {
                min: 0,
                max: null,
              },
              samplePercentage: 100,
            }
            setConnections({ ...connections, [conn.id]: conn })
          }}
          onEditEdge={(connection) => setActiveConnection(connection)}
          configuredNodes={Object.values(tasks)
            // When editing a task you can't press "Save" before the name is filled - so this is valid!
            .filter((task) => task.name && task.allowedGroupIds?.length)
            .map((task) => task.id)}
          onModeChange={() => setMode(mode === "node" ? "edge" : "node")}
          mode={mode}
          onChangeInNodeCoords={setNodeCoords}
          isDraft={isDraft}
        />
        <div className="AnalysisConfigurationPage-time-container">
          <p>{t("minimumDuration", { time: formatTime(minTime) })}</p>
        </div>
      </div>
      <p className="Pheading ConfigurationPage">{t("taskPerformerConstraints")}</p>
      <div className="ConfigurationPage-performerConstraints-button">
        <Button variant="link" size="lg" onClick={() => setShowPerformerConstraints(!showPerformerConstraints)}>
            {showPerformerConstraints ? <FaArrowUp className="fa-arrowUp-icon"/> : <FiPlus className="fi-plus-icon"/>}
        </Button>
      </div>
      {showPerformerConstraints && (
          <>
            <PerformerConstraints
              tasks={tasks}
              performerConstraints={performerConstraints}
              onConstraintsChange={setPerformerConstraints}
            />
          </>
        )}
        <hr />
    </ConfigContainer>
    {activeTask && (
        <Popup onCancel={() => setActiveTask(null)}>
          <AnalysisTaskConfig
            title={t("editTask")}
            isDefinition
            task={activeTask}
            submitHandler={(res) => {
              setTasks(addToMap(tasks, { ...activeTask, ...res }))
              setActiveTask(null)
            }}
            isDraft={isDraft}
            deleteHandler={null}
            cancelHandler={() => setActiveTask(null)}
          />
        </Popup>
      )}
      {addConfig && (
        <Popup nonScrollable onCancel={() => setAddConfig(false)}>
          <AnalysisGroupConfig
            analysisGroup={analysisGroupsDropdown[analysisGroupConfigId]}
            onClose={() => {
              setAddConfig(false)
              setAnalysisGroupConfigId(null)
            }}
            onSubmit={() => {
              setAddConfig(false)
              setAnalysisGroupConfigId(null)
            }}
          />
        </Popup>
      )}
      {activeConnection && (
        <Popup nonScrollable onCancel={() => setActiveConnection(null)}>
          <AnalysisConnectionConfig
            connection={activeConnection}
            taskFrom={tasks[activeConnection.prevId]}
            taskTo={tasks[activeConnection.nextId]}
            submitHandler={(res) => {
              setConnections(addToMap(connections, { ...activeConnection, ...res }))
              setActiveConnection(null)
            }}
            deleteHandler={() => {
              setConnections(removeFromMap(connections, activeConnection))
              setActiveConnection(null)
            }}
            cancelHandler={() => setActiveConnection(null)}
          />
        </Popup>
      )}
    </>
  )
}

